import { FIXTURE } from "@/data/fixtures";
import {
	getV2ProtocolActor,
	hasProtocolEnv,
	natToAmount,
} from "@/lib/protocol-actor";
import { useQuery } from "@tanstack/react-query";

export type DataSource = "live" | "fixture" | "error";

export type TreasuryOverviewUi = {
	surplusUsdm: number;
	savingsDepositedUsdm: number;
	totalDebtUsdm: number;
	vaultTvlUsd: number;
	collateralizationRatio: number;
	liquidatableVaultCount: number;
	activeVaultCount: number;
	oracleStale: boolean;
	byAsset: Array<{ asset: string; lockedUsd: number; debt: number }>;
	source: DataSource;
	errorMessage?: string;
};

export type TreasuryFlowUi = {
	id: string;
	kind: string;
	direction: "In" | "Out";
	token: string;
	amount: number;
	detail: string;
	/** Ledger-style origin (principal, vault#N, protocol, …) */
	fromAccount: string;
	/** Ledger-style destination */
	toAccount: string;
	initiator: string;
	vaultId: string | null;
};

function fixtureOverview(): TreasuryOverviewUi {
	return {
		surplusUsdm: 125_000,
		savingsDepositedUsdm: 40_000,
		totalDebtUsdm: FIXTURE.borrowVolumeUsd,
		vaultTvlUsd: FIXTURE.tvlUsd,
		collateralizationRatio: FIXTURE.tvlUsd / Math.max(1, FIXTURE.borrowVolumeUsd),
		liquidatableVaultCount: FIXTURE.liquidatable.length,
		activeVaultCount: FIXTURE.vaultCount,
		oracleStale: false,
		byAsset: [
			{ asset: "ICP", lockedUsd: FIXTURE.tvlUsd * 0.45, debt: 800_000 },
			{ asset: "ckBTC", lockedUsd: FIXTURE.tvlUsd * 0.35, debt: 700_000 },
			{ asset: "ckETH", lockedUsd: FIXTURE.tvlUsd * 0.2, debt: 440_000 },
		],
		source: "fixture",
	};
}

export function useTreasuryOverview() {
	return useQuery({
		queryKey: ["v2", "public", "treasuryOverview"],
		queryFn: async (): Promise<TreasuryOverviewUi> => {
			const actor = await getV2ProtocolActor();
			if (!actor) {
				if (!hasProtocolEnv()) return fixtureOverview();
				throw new Error("Protocol actor unavailable (check VITE_* env and host)");
			}
			const o = await actor.getTreasuryOverview();
			let activeVaultCount = 0;
			try {
				const s = await actor.getAnalyticsSummary();
				activeVaultCount = Number(s.activeVaults);
			} catch {
				/* optional */
			}
			return {
				surplusUsdm: natToAmount(o.surplusUsdmE8),
				savingsDepositedUsdm: natToAmount(o.savingsDepositedUsdmE8),
				totalDebtUsdm: natToAmount(o.totalDebtUsdmE8),
				vaultTvlUsd: natToAmount(o.vaultTvlUsdE8),
				collateralizationRatio: natToAmount(o.collateralizationRatioE8),
				liquidatableVaultCount: Number(o.liquidatableVaultCount),
				activeVaultCount,
				oracleStale: o.oracleStale,
				byAsset: o.vaultLockedByAsset.map((b) => ({
					asset: Object.keys(b.collateralType as object)[0] ?? "ICP",
					lockedUsd: natToAmount(b.lockedUsdE8),
					debt: natToAmount(b.debtE8),
				})),
				source: "live",
			};
		},
		staleTime: 20_000,
		retry: hasProtocolEnv() ? 1 : 0,
	});
}

/** Only-up Phase A (treasury MMK buy) vs Phase B (staker USDM yield). */
export function useSurplusPhase() {
	return useQuery({
		queryKey: ["v2", "public", "surplusPhase"],
		queryFn: async (): Promise<{
			phaseB: boolean;
			soldMmk: number;
			capMmk: number;
			source: DataSource;
		}> => {
			const actor = await getV2ProtocolActor();
			if (!actor) {
				return {
					phaseB: false,
					soldMmk: 0,
					capMmk: 95_000_000,
					source: hasProtocolEnv() ? "error" : "fixture",
				};
			}
			const t = await actor.getTokenomicsStatus();
			return {
				phaseB: Boolean(t.phaseB),
				soldMmk: natToAmount(t.soldMmkE8),
				capMmk: natToAmount(t.capMmkE8),
				source: "live",
			};
		},
		staleTime: 30_000,
		retry: hasProtocolEnv() ? 1 : 0,
	});
}

export type LiquiditySleeveUi = {
	opsIcp: number;
	treasuryMmk: number;
	ckBtc: number;
	ckEth: number;
	psmCkUsdc: number;
	gasFundingOpen?: boolean;
	minSurplusHealth: number;
	minOpsUsdmHealth: number;
	surplusMaster: number;
	opsUsdm: number;
	deskEnabled: boolean;
	deskFeeBps: number;
	rows: Array<{
		label: string;
		amount: string;
		note: string;
		tokenId: string;
	}>;
	source: DataSource;
};

function fixtureLiquidity(): LiquiditySleeveUi {
	return {
		opsIcp: 0,
		treasuryMmk: 0,
		ckBtc: 0,
		ckEth: 0,
		psmCkUsdc: 0,
		minSurplusHealth: 10_000,
		minOpsUsdmHealth: 5_000,
		surplusMaster: 0,
		opsUsdm: 0,
		deskEnabled: true,
		deskFeeBps: 30,
		rows: FIXTURE.treasury.map((r) => ({
			...r,
			tokenId: r.label.includes("ICP")
				? "ICP"
				: r.label.includes("MMK")
					? "MMK"
					: r.label.includes("ckBTC") || r.label.includes("BTC")
						? "ckBTC"
						: r.label.includes("ckETH") || r.label.includes("ETH")
							? "ckETH"
							: r.label.includes("ckUSDC") || r.label.includes("USDC")
								? "ckUSDC"
								: "USDM",
		})),
		source: "fixture",
	};
}

export function useLiquidityTreasury() {
	return useQuery({
		queryKey: ["v2", "public", "liquidityTreasury"],
		queryFn: async (): Promise<LiquiditySleeveUi> => {
			const actor = await getV2ProtocolActor();
			if (!actor) {
				if (!hasProtocolEnv()) return fixtureLiquidity();
				throw new Error("Protocol actor unavailable");
			}
			const t = await actor.getLiquidityTreasury();
			const opsIcp = natToAmount(t.opsIcpE8);
			const treasuryMmk = natToAmount(t.treasuryMmkE8);
			const ckBtc = natToAmount(t.ckBtcE8);
			const ckEth = natToAmount(t.ckEthE8);
			const psmCkUsdc = natToAmount(t.psmCkUsdcE8);
			const gasFundingOpen = Boolean(t.gasFundingOpen);
			return {
				opsIcp,
				treasuryMmk,
				ckBtc,
				ckEth,
				psmCkUsdc,
				gasFundingOpen,
				minSurplusHealth: natToAmount(t.minSurplusHealthE8),
				minOpsUsdmHealth: natToAmount(t.minOpsUsdmHealthE8),
				surplusMaster: natToAmount(t.surplusMasterE8),
				opsUsdm: natToAmount(t.opsUsdmE8),
				deskEnabled: Boolean(t.deskEnabled),
				deskFeeBps: Number(t.deskFeeBps),
				rows: [
					{
						label: "Ops ICP",
						amount: `${opsIcp.toLocaleString()} ICP`,
						note: "Gas / desk sleeve",
						tokenId: "ICP",
					},
					{
						label: "Treasury MMK",
						amount: `${treasuryMmk.toLocaleString()} MMK`,
						note: "Non-voting ops",
						tokenId: "MMK",
					},
					{
						label: "ckBTC sleeve",
						amount: `${ckBtc.toLocaleString()} ckBTC`,
						note: "Liquidity desk inventory",
						tokenId: "ckBTC",
					},
					{
						label: "ckETH sleeve",
						amount: `${ckEth.toLocaleString()} ckETH`,
						note: "Liquidity desk inventory",
						tokenId: "ckETH",
					},
				],
				source: "live",
			};
		},
		staleTime: 30_000,
		retry: hasProtocolEnv() ? 1 : 0,
	});
}

export type BadDebtSummaryUi = {
	openBadDebtUsdm: number;
	reserveUsdm: number;
	eventCount: number;
	source: DataSource;
};

export function useBadDebtSummary() {
	return useQuery({
		queryKey: ["v2", "public", "badDebtSummary"],
		queryFn: async (): Promise<BadDebtSummaryUi> => {
			const actor = await getV2ProtocolActor();
			if (!actor) {
				if (!hasProtocolEnv()) {
					return {
						openBadDebtUsdm: 0,
						reserveUsdm: 0,
						eventCount: 0,
						source: "fixture",
					};
				}
				throw new Error("Protocol actor unavailable");
			}
			const s = await actor.getBadDebtSummary();
			return {
				openBadDebtUsdm: natToAmount(s.openBadDebtE8),
				reserveUsdm: natToAmount(s.reserveE8),
				eventCount: Number(s.eventCount),
				source: "live",
			};
		},
		staleTime: 30_000,
		retry: hasProtocolEnv() ? 1 : 0,
	});
}

export function useTreasuryFlows(limit = 10, offset = 0) {
	return useQuery({
		queryKey: ["v2", "public", "treasuryFlows", limit, offset],
		queryFn: async (): Promise<{
			rows: TreasuryFlowUi[];
			total: number;
			source: DataSource;
		}> => {
			const actor = await getV2ProtocolActor();
			if (!actor) {
				if (!hasProtocolEnv()) return { rows: [], total: 0, source: "fixture" };
				throw new Error("Protocol actor unavailable");
			}
			const page = await actor.getTreasuryFlows(BigInt(offset), BigInt(limit));
			const rows = page.events.map((e) => {
				const rec = e as {
					id: bigint;
					kind: object;
					direction: object;
					token: string;
					amountE8: bigint;
					detail: string;
					initiator?: string;
					fromAccount?: string;
					toAccount?: string;
					vaultId?: [] | [bigint];
				};
				const vaultOpt = rec.vaultId;
				const vaultId =
					vaultOpt && vaultOpt.length > 0 ? String(vaultOpt[0]) : null;
				const initiator = rec.initiator ?? "";
				// Prefer protocol from/to; fall back for older canisters without fields
				const fromAccount =
					typeof rec.fromAccount === "string" && rec.fromAccount.length > 0
						? rec.fromAccount
						: initiator || "—";
				const toAccount =
					typeof rec.toAccount === "string" && rec.toAccount.length > 0
						? rec.toAccount
						: vaultId
							? `vault#${vaultId}`
							: "protocol";
				return {
					id: String(rec.id),
					kind: Object.keys(rec.kind as object)[0] ?? "Other",
					direction:
						"In" in rec.direction ? ("In" as const) : ("Out" as const),
					token: rec.token,
					amount: natToAmount(rec.amountE8),
					detail: rec.detail,
					fromAccount,
					toAccount,
					initiator,
					vaultId,
				};
			});
			return {
				rows,
				total: Number(page.total),
				source: "live",
			};
		},
		staleTime: 15_000,
		retry: hasProtocolEnv() ? 1 : 0,
	});
}
